import type { FormEvent } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatDate } from '@formatters/date.formatter';
import { isAfterDate } from '@helpers/date/isAfter.helper';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import {
  createInterventionSchema,
  type CreateInterventionDto,
  type CreateInterventionPayload,
} from '@services/interventions/dtos/create.dto';
import { interventionService } from '@services/interventions/service';
import type { GetServiceRequestByIdOutput } from '@services/serviceRequests/types/getById.type';
import { getAllServicesToSelectSchema } from '@services/services/dtos/getAllToSelect.dto';
import { serviceService } from '@services/services/service';

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/interventions/create',
)({
  component: ServiceRequestInterventionsCreate,
  validateSearch: search => getAllServicesToSelectSchema.parse(search),
  loaderDeps: ({ search }) => search,
});

function ServiceRequestInterventionsCreate() {
  const { id: serviceRequestId } = Route.useParams();
  const { businessLineId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const queryClient = useQueryClient();

  const { data: services, isFetching: isServicesPending } = useQuery({
    queryKey: queryKeys.services.select(businessLineId),
    queryFn: () => serviceService.getAllToSelect({ businessLineId }),
    staleTime: EStaleTime.OneHour,
  });

  const form = useForm<CreateInterventionDto>({
    resolver: zodResolver(createInterventionSchema),
    mode: 'onBlur',
    defaultValues: {
      visitDate: '',
      workDate: '',
      workEndDate: '',
    },
  });

  const visitDate = useWatch({
    control: form.control,
    name: 'visitDate',
  });
  const workDate = useWatch({
    control: form.control,
    name: 'workDate',
  });

  const handleVisitDateChange = (event: FormEvent<HTMLInputElement>) => {
    const isWorkDateAfterVisit = isAfterDate(workDate, {
      dateToCompare: event.currentTarget.value,
    });

    if (!isWorkDateAfterVisit) {
      form.setValue('workDate', '', { shouldValidate: true });
      form.setValue('workEndDate', '', { shouldValidate: true });
    }
  };

  const { isPending, mutate: createIntervention } = useMutation<
    void,
    unknown,
    CreateInterventionPayload
  >({
    mutationFn: interventionService.create,
    onError: handleRequestError,
    onSuccess: async () => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.serviceRequests.detail(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });
      queryClient.setQueryData<GetServiceRequestByIdOutput>(
        queryKeys.serviceRequests.detail(serviceRequestId),
        oldData => {
          if (!oldData?.serviceRequest.counts) {
            return oldData;
          }

          return {
            serviceRequest: {
              ...oldData.serviceRequest,
              counts: {
                ...oldData.serviceRequest.counts,
                unassignedInterventions:
                  (oldData.serviceRequest.counts.unassignedInterventions ?? 0) +
                  1,
              },
            },
          };
        },
      );

      invalidateSmartEventTimeline({
        id: serviceRequestId,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });

      navigate({
        to: '/service-requests/$id',
        params: { id: serviceRequestId },
        replace: true,
      });
      toast.success('Success', {
        description: 'Intervention created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createIntervention({
      serviceRequestId,
      workDate: values.workDate,
      workEndDate: values.workEndDate,
      visitDate: values.visitDate,
      serviceId: values.serviceId,
      remark: values.remark,
      invoiceAmount: values.invoiceAmount,
      quoteAmount: values.quoteAmount,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Create intervention"
        to="/service-requests/$id"
      />

      <form
        className="mx-auto mt-8 grid w-full space-y-2 xl:w-4/5 2xl:w-4/6"
        onSubmit={handleSubmit}
      >
        <InputControlled
          isLoading={isServicesPending}
          label="Service"
          name="serviceId"
          options={services?.list || []}
          variant="select"
        />

        <span className="text-input my-2 text-center text-sm font-light">
          Attention: The provider will be later assigned to this intervention
          through Smart Match
        </span>

        <InputControlled
          label="Remark"
          maxLength={1000}
          name="remark"
          rows={8}
          showCount
          variant="textarea"
        />

        <Separator variant="form" />

        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-2">
          <InputControlled
            label="Visit date"
            name="visitDate"
            onChangeCapture={handleVisitDateChange}
            type="date"
            variant="base"
          />
          <InputControlled
            label="Quote amount"
            name="quoteAmount"
            prefix="€"
            variant="money"
          />
        </div>

        <Separator variant="form" />

        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-3">
          <InputControlled
            disabled={
              !visitDate ||
              isAfterDate(visitDate, { dateToCompare: workDate, daysToAdd: 1 })
            }
            label="Work start date"
            min={formatDate(visitDate, {
              format: 'YYYY-MM-DD',
              daysToAdd: 1,
            })}
            name="workDate"
            type="date"
          />
          <InputControlled
            disabled={!visitDate || !workDate}
            label="Work end date"
            min={formatDate(workDate || visitDate, { format: 'YYYY-MM-DD' })}
            name="workEndDate"
            type="date"
          />
          <InputControlled
            label="Invoice amount"
            name="invoiceAmount"
            prefix="€"
            variant="money"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id: serviceRequestId }}
            rounded="lg"
            size="lg"
            to="/service-requests/$id"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
